import { z } from "zod";

/**
 * Perguntas de boas-vindas (depois de criar a conta, no estilo do Cursor): o papel da pessoa, para
 * que vai usar o Kompil, com o que mexe e onde ouviu falar dele. As opções ficam aqui para a API
 * validar e a interface mostrar as mesmas, com os mesmos rótulos.
 */
export interface OnboardingOption<T extends string = string> {
  id: T;
  label: string;
}

export const ONBOARDING_ROLES = [
  { id: "software", label: "Engenharia de software" },
  { id: "design", label: "Design" },
  { id: "produto", label: "Gestão de produto" },
  { id: "pesquisa", label: "Pesquisa" },
  { id: "dados", label: "Ciência de dados" },
  { id: "estudante", label: "Estudante" },
  { id: "marketing", label: "Marketing" },
  { id: "operacoes", label: "Operações" },
  { id: "outro", label: "Outra coisa" },
] as const satisfies readonly OnboardingOption[];

export const ONBOARDING_PURPOSES = [
  { id: "equipe", label: "Programar com o meu time" },
  { id: "pessoal", label: "Projetos pessoais" },
  { id: "estudo", label: "Estudar e aprender" },
  { id: "ensino", label: "Dar aula ou mentoria" },
  { id: "freelance", label: "Freelas e clientes" },
  { id: "open-source", label: "Open source" },
  { id: "hackathon", label: "Hackathons e eventos" },
  { id: "conhecer", label: "Conhecer outros devs" },
  { id: "outro", label: "Outra finalidade" },
] as const satisfies readonly OnboardingOption[];

export const ONBOARDING_AREAS = [
  { id: "frontend", label: "Front-end" },
  { id: "backend", label: "Back-end e APIs" },
  { id: "mobile", label: "Mobile" },
  { id: "dados-ia", label: "Dados e IA" },
  { id: "devops", label: "DevOps e nuvem" },
  { id: "jogos", label: "Jogos" },
  { id: "embarcados", label: "Embarcados e IoT" },
  { id: "seguranca", label: "Segurança" },
  { id: "desktop", label: "Apps desktop" },
] as const satisfies readonly OnboardingOption[];

export const ONBOARDING_SOURCES = [
  { id: "indicacao", label: "Indicação de alguém" },
  { id: "youtube", label: "YouTube" },
  { id: "instagram", label: "Instagram" },
  { id: "tiktok", label: "TikTok" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "x", label: "X (Twitter)" },
  { id: "busca", label: "Google ou outra busca" },
  { id: "github", label: "GitHub" },
  { id: "comunidade", label: "Comunidade, Discord ou fórum" },
  { id: "evento", label: "Evento, curso ou faculdade" },
  { id: "ia", label: "Recomendação de uma IA" },
  { id: "outro", label: "Outro lugar" },
] as const satisfies readonly OnboardingOption[];

const ids = <T extends readonly { id: string }[]>(list: T) =>
  list.map((o) => o.id) as unknown as [T[number]["id"], ...T[number]["id"][]];

export const OnboardingRole = z.enum(ids(ONBOARDING_ROLES));
export const OnboardingPurpose = z.enum(ids(ONBOARDING_PURPOSES));
export const OnboardingArea = z.enum(ids(ONBOARDING_AREAS));
export const OnboardingSource = z.enum(ids(ONBOARDING_SOURCES));

const many = <T extends z.ZodType<string>>(item: T, max: number, message: string) =>
  z
    .array(item)
    .min(1, message)
    .max(max)
    .transform((list) => [...new Set(list)]);

export const OnboardingInput = z.object({
  /** Com o que trabalha (uma ou mais respostas). */
  roles: many(OnboardingRole, ONBOARDING_ROLES.length, "Escolha pelo menos uma opção."),
  /** Para que vai usar o Kompil (uma ou mais respostas). */
  purposes: many(OnboardingPurpose, ONBOARDING_PURPOSES.length, "Escolha pelo menos um objetivo."),
  /** Com o que mexe (uma ou mais áreas). */
  areas: z
    .array(OnboardingArea)
    .min(1, "Escolha pelo menos uma área.")
    .max(ONBOARDING_AREAS.length)
    .transform((list) => [...new Set(list)]),
  /** Ferramentas e linguagens do dia a dia, em texto livre (opcional). */
  stack: z.string().trim().max(200).optional(),
  source: OnboardingSource,
  /** Detalhe de "onde ouviu" (qual pessoa, canal, evento…). */
  sourceDetail: z.string().trim().max(200).optional(),
});
export type OnboardingInput = z.infer<typeof OnboardingInput>;

/** Rótulo de uma opção (painel da plataforma, exportação). */
export function onboardingLabel(
  list: readonly OnboardingOption[],
  id: string | null | undefined,
): string | null {
  if (!id) return null;
  return list.find((o) => o.id === id)?.label ?? id;
}
